import React from 'react';

import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {STATUS_COLOR_LIST} from '@consts/statuses';
import {TaxpayerDeclarationGate, taxpayerDetailsModel} from '@entities/taxpayers/detail-model';
import {GridFieldConfig} from '@shared/types/grid';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';
import {formatNumberWithFraction} from '@utils/number';

import {declarationStatusColor, formatAmount} from './helpers';

const config = (t: any): GridFieldConfig[] => [
    {
        id: 'correctNum',
        header: t('page_taxpayers.table.correction_number'),
        minWidth: 180,
        isSortable: true,
    },
    {id: 'filingDate', header: t('page_taxpayers.table.correction_date'), minWidth: 180},
    {id: 'decType', header: t('page_taxpayers.table.dec_type'), minWidth: 180},
    {id: 'isDecFiled', header: t('page_taxpayers.table.dec_status'), minWidth: 180},
    {id: 'taxGapType', header: t('page_taxpayers.table.gap_type'), minWidth: 180},
    {
        id: 'costSalesWithNds',
        header: t('page_taxpayer.declaration.cost_sales_with_nds'),
        minWidth: 220,
        isSortable: true,
    },
    {
        id: 'costPurchasesWithNds',
        header: t('page_taxpayer.declaration.cost_purchases_with_nds'),
        minWidth: 220,
        isSortable: true,
    },
    {
        id: 'sumPayNds',
        header: t('page_taxpayer.declaration.sum_pay_nds'),
        minWidth: 220,
        isSortable: true,
    },
    {
        id: 'sumNdsDeduction',
        header: t('page_taxpayer.declaration.sum_nds_deduction'),
        minWidth: 220,
        isSortable: true,
    },
    {
        id: 'totalSumNds',
        header: t('page_taxpayer.declaration.total_sum_nds'),
        minWidth: 220,
        isSortable: true,
    },
    {
        id: 'ndsDeductionPercentage',
        header: t('page_taxpayer.declaration.nds_deduction_percent'),
        minWidth: 220,
        isSortable: true,
    },
    {id: 'sumPay', header: t('page_taxpayer.declaration.sum_pay'), minWidth: 220, isSortable: true},
];

const gapTypeLabels: Record<string, string> = {
    DECLARATION_NOT_FILED: 'terms.declaration.gap_type.dec_not_filled',
    NDS_NOT_PAID: 'terms.declaration.gap_type.tax_not_paid',
    INVOICE_DIFFER: 'terms.declaration.gap_type.invoice_differ',
};

const gapTypeColors = {
    DECLARATION_NOT_FILED: STATUS_COLOR_LIST.ORANGE,
    NDS_NOT_PAID: STATUS_COLOR_LIST.RED,
    INVOICE_DIFFER: STATUS_COLOR_LIST.YELLOW,
    NO_GAP: STATUS_COLOR_LIST.GREEN,
} as const;

const declarationTypeLabel = (value: number | string | null) => {
    if (value === 0 || value === 'NOT_FILLED' || value === 'NOT_FILED')
        return 'terms.declaration.type.not_filled';
    if (value === 1 || value === 'PAYABLE') return 'terms.declaration.type.payable';
    if (value === 2 || value === 'DEDUCTIBLE' || value === 'DEDUCTION')
        return 'terms.declaration.type.deduction';
    if (value === 3 || value === 'ZERO') return 'terms.declaration.type.zero';
    return '';
};

export const DeclarationTab = () => {
    const {tin} = useParams<{tin: string}>();
    const {t} = useTranslation();
    const taxpayer = useUnit(taxpayerDetailsModel);

    useGate(TaxpayerDeclarationGate, {tin});

    const data = (taxpayer.state.declarations?.data ?? []).map((row: any) => {
        const gapTypeKey =
            row.taxGapType && row.taxGapType in gapTypeColors
                ? (row.taxGapType as keyof typeof gapTypeColors)
                : 'NO_GAP';

        return {
            ...row,
            filingDate: row.filingDate ? format(new Date(row.filingDate), 'dd.MM.yyyy') : '—',
            decType: declarationTypeLabel(row.decType) ? t(declarationTypeLabel(row.decType)) : '—',
            isDecFiled: (
                <StatusWrapper color={declarationStatusColor(row.isDecFiled)}>
                    {row.isDecFiled === true || row.isDecFiled === 1
                        ? t('terms.declaration.status.filled')
                        : t('terms.declaration.status.not_filled')}
                </StatusWrapper>
            ),
            taxGapType: (
                <StatusWrapper color={gapTypeColors[gapTypeKey]}>
                    {gapTypeKey === 'NO_GAP'
                        ? t('terms.declaration.gap_type.no_gap')
                        : t(gapTypeLabels[gapTypeKey])}
                </StatusWrapper>
            ),
            costSalesWithNds: formatAmount(row.costSalesWithNds),
            costPurchasesWithNds: formatAmount(row.costPurchasesWithNds),
            sumPayNds: formatAmount(row.sumPayNds),
            sumNdsDeduction: formatAmount(row.sumNdsDeduction),
            totalSumNds: formatAmount(row.totalSumNds),
            ndsDeductionPercentage: `${formatNumberWithFraction(
                row.sumPayNds
                    ? (Number(row.sumNdsDeduction ?? 0) / Number(row.sumPayNds)) * 100
                    : 0,
                {minimumFractionDigits: 0, maximumFractionDigits: 0},
            )}%`,
            sumPay: formatAmount(row.sumPay),
        };
    });

    return (
        <Table
            columns={mapTableColumns(
                config(t),
                undefined,
                undefined,
                undefined,
                taxpayer.state.declarationSortings,
                (items) =>
                    taxpayer.declarationSortingsChanged(
                        items.reduce((acc, item) => ({...acc, [item.id]: item.value}), {}),
                    ),
            )}
            data={data}
            isLoading={taxpayer.state.pending}
            hasNext={Boolean(taxpayer.state.declarations?.nextPageRef)}
            pageIndex={taxpayer.state.declarationPage}
            onPageChange={taxpayer.declarationPageChanged}
            onPageSizeChange={taxpayer.declarationPageSizeChanged}
        />
    );
};
